import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, alpha } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { jbColors } from 'app/theme/jbColors';
import {
  selectEbkServices,
  selectIndiaMigrationService,
  selectLastAccessedEBankingService,
  selectMarketLinkService,
  selectMigrationCompleteService,
  selectMigrationCounts,
} from 'features/entities/entitiesSelectors';
import { Toolbar } from 'features/toolbar/Toolbar';
import { openExternalServiceStart } from 'features/extNavigation/extNavigationSlice';

import { EBankingServiceCard } from '.';
import background from 'common/assets/jb-hq-background.jpg';
import { ServiceCode, ServiceCodes } from 'features/entities/entitiesTypes';
import { MigrationStartServiceCard } from 'features/home/MigrationServiceCard/MigrationStartServiceCard';
import { MigrationInProgressServiceCard } from 'features/home/MigrationServiceCard/MigrationInProgressServiceCard';
import { MigrationProductTourCard } from 'features/home/MigrationServiceCard/MigrationProductTourCard';
import { MigrationCompleteServiceCard } from 'features/home/MigrationServiceCard/MigrationCompleteServiceCard';
import { MigrationIndiaServiceCard } from 'features/home/MigrationServiceCard/MigrationIndiaServiceCard';
import { isMigrationInstance } from 'common/utils/migrationUtils';
import { REGULAR_API_URL } from 'app/config';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      backgroundImage: `url(${background})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundAttachment: 'scroll',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'stretch',
      position: 'relative',
    },

    top: {
      flex: '1.84 0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: alpha(theme.palette.common.black, 0.3),
    },

    bottom: {
      flex: '1 0 auto',
      backgroundColor: jbColors.app.almostWhite,
    },

    content: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      overflowY: 'auto',
      '-webkit-overflow-scrolling': 'touch',
      zIndex: 1,
    },

    toolbarContainer: {
      width: '100%',
      flex: '0 0 auto',
      color: theme.palette.common.white,
    },

    serviceContainer: {
      flex: '0.9',
      display: 'flex',
      flexDirection: 'column',
    },

    title: {
      fontWeight: 'bold',
      fontSize: 16,
      lineHeight: '22px',
      letterSpacing: '-0.2px',
      marginTop: theme.spacing(3.5),
      marginBottom: theme.spacing(1.5),
    },

    label: {
      fontWeight: 300,
      lineHeight: '22px',
      letterSpacing: '-0.408px',
      marginBottom: theme.spacing(2.5),
    },

    whiteOutline: {
      borderColor: theme.palette.common.white,
      color: theme.palette.common.white,
    },

    marginTop: {
      marginTop: theme.spacing(8),
    },
    serviceLinkLabel: {
      fontWeight: 'bold',
      fontSize: 14,
      lineHeight: 1.5,
      letterSpacing: '-0.32px',
      paddingLeft: theme.spacing(0.5),
    },

    ebkServicesList: {
      padding: 0,
      margin: 0,
      listStyle: 'none',
    },

    marketLinkButton: {
      marginTop: theme.spacing(3),
      fontSize: theme.typography.pxToRem(13),
      letterSpacing: '0.1em',
    },
  })
);

const sortedEbkServiceCodes: ServiceCode[] = [
  ServiceCodes.DIG_SG,
  ServiceCodes.DIG_HK,
  ServiceCodes.EBK,
  ServiceCodes.EBK_R20,
  ServiceCodes.EL2_LU,
  ServiceCodes.EL2_MC,
  ServiceCodes.INO2,
  ServiceCodes.KOS,
  ServiceCodes.LSB_APAC_MOBILE,
];

export const HomeEBankingActivated = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [oneCardTopMargin, setOneCardTopMargin] = useState(0);

  const firstCardRef = useRef<HTMLLIElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const lastAccessedEBankingService = useSelector(selectLastAccessedEBankingService);
  const ebkServices = useSelector(selectEbkServices).sort((a, b) => {
    // lastAccessedEBankingService goes first
    if (a.code === lastAccessedEBankingService) {
      return -1;
    }
    return sortedEbkServiceCodes.indexOf(a.code) - sortedEbkServiceCodes.indexOf(b.code);
  });
  const showMigrationIndiaTile = Boolean(useSelector(selectIndiaMigrationService));

  const migrationCounts = useSelector(selectMigrationCounts);
  const migrationStartCount = migrationCounts[0];
  const migrationCompleteCount = migrationCounts[1];
  const showStartTile = migrationStartCount > 0 && migrationCompleteCount === 0;
  const showInProgressTile = migrationStartCount > 0 && migrationCompleteCount > 0;
  const migrationCompleteService = useSelector(selectMigrationCompleteService);
  const showCompleteTile = migrationStartCount === 0 && migrationCompleteService;
  const hasMigrationService =
    showStartTile || showInProgressTile || showCompleteTile || showMigrationIndiaTile;

  const marketLinkService = useSelector(selectMarketLinkService);

  const handleMarketLink = () => {
    dispatch(openExternalServiceStart(marketLinkService!));
  };

  const ebkServiceCount = ebkServices.length;
  const migrationServiceCount = hasMigrationService ? 1 : 0;
  const serviceCount = ebkServiceCount + migrationServiceCount;

  // Calculate margin top for single card
  useEffect(() => {
    if (serviceCount === 1 && toolbarRef.current && firstCardRef.current && topRef.current) {
      const topMargin =
        topRef.current.offsetHeight -
        toolbarRef.current.offsetHeight -
        firstCardRef.current.offsetHeight / 2;
      setOneCardTopMargin(topMargin);
    }
  }, [ebkServices, serviceCount, setOneCardTopMargin]);

  useEffect(() => {
    if (isMigrationInstance()) {
      window.location.replace(REGULAR_API_URL!);
    }
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.top} ref={topRef}></div>
      <div className={classes.bottom}></div>
      <div className={classes.content}>
        <div className={classes.toolbarContainer} ref={toolbarRef}>
          <Toolbar />
        </div>
        <div
          className={classes.serviceContainer}
          style={{
            // if we have one EBK we have special positioning for its card
            justifyContent: serviceCount === 1 ? 'center' : 'flex-end',
          }}
        >
          <ul
            className={classes.ebkServicesList}
            style={{
              // if we have one EBK we have special positioning for its card
              marginTop: serviceCount === 1 ? oneCardTopMargin : 0,
            }}
          >
            {showStartTile && (
              <>
                <MigrationProductTourCard />
                <MigrationStartServiceCard />
              </>
            )}
            {showInProgressTile && (
              <>
                <MigrationProductTourCard />
                <MigrationInProgressServiceCard
                  migrationStartCount={migrationStartCount}
                  migrationCompleteCount={migrationCompleteCount}
                />
              </>
            )}
            {showCompleteTile && (
              <MigrationCompleteServiceCard service={migrationCompleteService} />
            )}
            {showMigrationIndiaTile && <MigrationIndiaServiceCard />}
            {ebkServices.map((service, idx) => (
              <EBankingServiceCard
                ref={idx === 0 ? firstCardRef : null}
                key={`ebanking-card-${idx}`}
                service={service}
                size={idx === 0 && !hasMigrationService ? 'normal' : 'small'}
              />
            ))}
          </ul>
        </div>
        {marketLinkService && (
          <Button
            className={classes.marketLinkButton}
            variant="text"
            color="primary"
            onClick={handleMarketLink}
          >
            {t('home.actions.marketLink')}
          </Button>
        )}
      </div>
    </div>
  );
};
